import type { AchievementsState } from "@/features/achievements/types";
import { normalizeAchievementsState } from "@/features/achievements/achievementIdAliases";
import type { HoverIdlePaymentState } from "@/features/payment/types";
import { emptyRecordsState, type RecordsState } from "@/features/records/types";
import {
  emptyHovSkillState,
  type HovSkillState,
} from "@/features/hoverIdle/hov/hovSkillModel";
import { resolveHovSkillEffects } from "@/features/hoverIdle/hov/hovSkillEffects";
import {
  emptyTimSkillState,
  type TimSkillState,
} from "@/features/hoverIdle/tim/timSkillModel";
import {
  createDefaultNHovState,
  migrateLegacyNHovBranchEffectOverrides,
} from "@/features/hoverIdle/nHov/nHovLogic";
import {
  normalizeNHovBranchExperienceByBranch,
  normalizeLegacyNHovBranchEffectOverrides,
  normalizeNHovContinuousEffectProgressByBranch,
  normalizeNHovEffectBoundaryCaps,
} from "@/features/hoverIdle/nHov/nHovBranches";
import { normalizeNHovThemeHue } from "@/features/hoverIdle/nHov/nHovCandidateTheme";
import {
  createDefaultOHovState,
  isOHovUnlocked,
  normalizeOHovRunRecords,
} from "@/features/hoverIdle/oHov/oHovLogic";
import { normalizeHelpTopicIds } from "@/features/help/helpTopics";
import {
  cloneMpmState,
  createDefaultMpmState,
  normalizeMpmState,
  type MpmMHovExecutionState,
  type MpmExplorationState,
  type MpmNHovExecutionConditionState,
  type MpmNHovExecutionState,
  type MpmOHovExecutionState,
  type MpmPresetSnapshot,
  type MpmState,
} from "@/features/hoverIdle/mpm/mpmState";
import {
  cloneExplorationState,
  createDefaultExplorationState,
  normalizeExplorationState,
} from "@/features/hoverIdle/exploration/explorationState";
import type { ExplorationState } from "@/features/hoverIdle/exploration/explorationTypes";
import {
  HOV_AMPLIFIER_ENTRY_IDS,
  createInitialHovAmplifierState,
  normalizeHovAmplifierState,
  type HovAmplifierEntryId,
  type HovAmplifierState,
} from "@/features/hoverIdle/hov/hovAmplifierModel";
import { normalizeWallState } from "@/features/hoverIdle/wall/wallState";
import {
  normalizeMpWallAssignments,
  type MpWallAssignments,
} from "@/features/hoverIdle/wall/mpWallAssignments";
import {
  DEFAULT_WALL_START_CHARGE_MS,
  isWallStartChargeMs,
} from "@/features/hoverIdle/wall/wallStartCharge";
import {
  HOV_CELL_BASE_COSTS,
  HOV_CELL_GAIN_UPGRADE_BASE_COST,
  TIM_CELL_BASE_COSTS,
  resolveInitialHovCellUpgradeCost,
} from "@/features/hoverIdle/core/hoverIdleState";
import {
  deserializeHuge,
  serializeHuge,
  type HugeNumber,
} from "@/shared/numbers";
import type {
  HoverIdleHovCellState,
  HoverIdleHovTargetGridState,
  HoverIdleModelState,
  HoverIdleTimCellState,
  HoverIdleTimTargetGridState,
  MHovState,
  NHovBranchId,
  NHovEffectBoundaryCaps,
  NHovState,
  OHovState,
} from "../types";
import {
  cloneOfflineRewardSaveMetadata,
  createDefaultOfflineRewardSaveMetadata,
  type OfflineRewardSaveMetadata,
} from "./offlineRewardSaveMetadata";

export const HOVER_IDLE_SAVE_DATA_VERSION = 2;
export const PREVIOUS_HOVER_IDLE_SAVE_DATA_VERSION = 1;
export const LEGACY_HOVER_IDLE_SAVE_DATA_VERSION = 0;

export const defaultHovTimBalanceSaveState = {
  total: 2,
  unassigned: 0,
  hovAllocation: 0,
  timAllocation: 2,
  hovGainAllocation: 0,
  timGainAllocation: 0,
} as const;

export const defaultHiddenGrowthSaveState = {
  hovGainBonus: "0",
  timGainBonus: "0",
  hovTimProgress: 0,
} as const;

export interface HoverIdleSaveDataV2 {
  readonly version: typeof HOVER_IDLE_SAVE_DATA_VERSION;
  readonly economy: HoverIdleSaveEconomyState;
  readonly settings: HoverIdleModelState["settings"];
  readonly cumulativeStats: HoverIdleSaveCumulativeStatsState;
  readonly payment: HoverIdlePaymentState;
  readonly skills: HoverIdleSaveSkillsState;
  readonly achievements: AchievementsState;
  readonly records: RecordsState;
  readonly timestamps: HoverIdleSaveTimestamps;
  readonly offlineReward: OfflineRewardSaveMetadata;
}

export interface HoverIdleSaveSkillsState {
  readonly hovAcquiredIds: HovSkillState["acquiredIds"];
  readonly timAcquiredIds: TimSkillState["acquiredIds"];
}

export interface HoverIdleSaveTimestamps {
  readonly savedAt: number;
  readonly firstPlayedAt: number | null;
}

export type HoverIdleSaveData = HoverIdleSaveDataV2;

type HoverIdleSaveHugeValue = unknown;

export interface HoverIdleSaveHiddenGrowthState extends Omit<
  HoverIdleModelState["economy"]["hiddenGrowth"],
  "hovGainBonus" | "timGainBonus"
> {
  readonly hovGainBonus: HoverIdleSaveHugeValue;
  readonly timGainBonus: HoverIdleSaveHugeValue;
}

export interface HoverIdleSaveHovCellState extends Omit<
  HoverIdleHovCellState,
  "hovGainMultiplier" | "upgradeCost"
> {
  readonly hovGainMultiplier: HoverIdleSaveHugeValue;
  readonly upgradeCost: HoverIdleSaveHugeValue;
}

export interface HoverIdleSaveTimCellState extends Omit<
  HoverIdleTimCellState,
  "timGainMultiplier" | "upgradeCost"
> {
  readonly timGainMultiplier: HoverIdleSaveHugeValue;
  readonly upgradeCost: HoverIdleSaveHugeValue;
}

export interface HoverIdleSaveTargetGridState extends Omit<
  HoverIdleHovTargetGridState,
  "hovCells"
> {
  readonly hovCells: readonly HoverIdleSaveHovCellState[];
}

export interface HoverIdleSaveTimTargetGridState {
  readonly gridTier: HoverIdleTimTargetGridState["gridTier"];
  readonly timCells: readonly HoverIdleSaveTimCellState[];
}

export interface HoverIdleSaveMHovState extends Omit<
  MHovState,
  | "hgMultiplier"
  | "currentLoopHovEarned"
  | "maxHovSinceLastMHov"
  | "lastRewardBaseHov"
> {
  readonly hgMultiplier: HoverIdleSaveHugeValue;
  readonly currentLoopHovEarned: HoverIdleSaveHugeValue;
  readonly maxHovSinceLastMHov: HoverIdleSaveHugeValue;
  readonly lastRewardBaseHov?: HoverIdleSaveHugeValue;
}

export type HoverIdleSaveNHovBranchExperienceByBranch = Readonly<
  Record<NHovBranchId, HoverIdleSaveHugeValue>
>;

export type HoverIdleSaveNHovBranchEffectOverrides = Readonly<
  Partial<Record<NHovBranchId, HoverIdleSaveHugeValue>>
>;

export interface HoverIdleSaveNHovState extends Omit<
  NHovState,
  "branchExperienceByBranch" | "effectBoundaryCaps"
> {
  readonly branchExperienceByBranch?: HoverIdleSaveNHovBranchExperienceByBranch;
  readonly effectBoundaryCaps?: NHovEffectBoundaryCaps;
  readonly branchEffectOverrides?: HoverIdleSaveNHovBranchEffectOverrides;
}

export interface HoverIdleSaveOHovRunRecords {
  readonly currentRunStartedAtMs: number;
  readonly lastRunDurationMs: number | null;
  readonly fastestRunDurationMs: number | null;
  // Game-time fields added so admin/system speed cannot trivialize the
  // fast/quick OHov achievements. Optional for backward compatibility with
  // saves written before the field was introduced.
  readonly currentRunGameElapsedMs?: number;
  readonly fastestRunGameDurationMs?: number | null;
}

export interface HoverIdleSaveOHovState extends Omit<
  OHovState,
  "isUnlocked" | "power" | "hovCore" | "records"
> {
  readonly isUnlocked?: boolean;
  readonly power: HoverIdleSaveHugeValue;
  readonly hovCore: HoverIdleSaveHugeValue;
  readonly records?: HoverIdleSaveOHovRunRecords;
}

export interface HoverIdleSaveHovAmplifierEntryState {
  readonly id: HovAmplifierEntryId;
  readonly gaugeValue: number;
  readonly enhancementLevel: number;
}

export interface HoverIdleSaveHovAmplifierState {
  readonly ap: HoverIdleSaveHugeValue;
  readonly apExponent: number;
  readonly entries: readonly HoverIdleSaveHovAmplifierEntryState[];
  // Optional so legacy saves restore with zeroed cumulative boosts.
  readonly cumulativeBoosts?: Partial<
    Record<HovAmplifierEntryId, HoverIdleSaveHugeValue>
  >;
}

export interface HoverIdleSaveMpmMHovExecutionState extends Omit<
  MpmMHovExecutionState,
  "multiplier"
> {
  readonly multiplier: HoverIdleSaveHugeValue;
}

export interface HoverIdleSaveMpmNHovExecutionConditionState extends Omit<
  MpmNHovExecutionConditionState,
  "minimumEffect"
> {
  readonly minimumEffect: HoverIdleSaveHugeValue;
}

export interface HoverIdleSaveMpmNHovExecutionState extends Omit<
  MpmNHovExecutionState,
  "conditions"
> {
  readonly conditions: Readonly<
    Record<NHovBranchId, HoverIdleSaveMpmNHovExecutionConditionState>
  >;
}

export interface HoverIdleSaveMpmOHovExecutionState extends Omit<
  MpmOHovExecutionState,
  "minimumHovCore"
> {
  readonly minimumHovCore: HoverIdleSaveHugeValue;
}

export interface HoverIdleSaveMpmExplorationState extends Omit<
  MpmExplorationState,
  "enabled"
> {
  readonly enabled?: boolean;
}

export interface HoverIdleSaveMpmPresetNumericSettings {
  readonly mhovExecution: {
    readonly multiplier: HoverIdleSaveHugeValue;
    readonly minimumResetIntervalMs: number;
  };
  readonly nhovExecution: {
    readonly conditions: Readonly<
      Record<NHovBranchId, HoverIdleSaveMpmNHovExecutionConditionState>
    >;
  };
  readonly ohovExecution: {
    readonly minimumHovCore: HoverIdleSaveHugeValue;
    readonly minimumIntervalMs: number;
  };
}

export interface HoverIdleSaveMpmPresetSnapshot extends Omit<
  MpmPresetSnapshot,
  "numericSettings"
> {
  readonly numericSettings?: HoverIdleSaveMpmPresetNumericSettings | null;
}

export type HoverIdleSaveMpmPresetSnapshotSlot =
  HoverIdleSaveMpmPresetSnapshot | null;

export interface HoverIdleSaveMpmState extends Omit<
  MpmState,
  | "mhovExecution"
  | "nhovExecution"
  | "ohovExecution"
  | "exploration"
  | "presets"
> {
  readonly mhovExecution: HoverIdleSaveMpmMHovExecutionState;
  readonly nhovExecution: HoverIdleSaveMpmNHovExecutionState;
  readonly ohovExecution: HoverIdleSaveMpmOHovExecutionState;
  readonly exploration?: HoverIdleSaveMpmExplorationState;
  readonly presets?: readonly HoverIdleSaveMpmPresetSnapshotSlot[];
}

export interface HoverIdleSaveEconomyState extends Omit<
  HoverIdleModelState["economy"],
  | "hov"
  | "tim"
  | "hovGainPerSecond"
  | "timGainPerSecond"
  | "hiddenGrowth"
  | "upgradeCost"
  | "timUpgradeCost"
  | "targetGrid"
  | "minHovGainUnlockCostsByTargetIndex"
  | "minHovGainMilestoneTriggerCostsByMilestoneIndex"
  | "timTargetGrid"
  | "mHov"
  | "nHov"
  | "oHov"
  | "lastAnyResetAtMs"
  | "mpm"
  | "hovAmplifier"
  | "wall"
  | "exploration"
> {
  readonly hov: HoverIdleSaveHugeValue;
  readonly tim: HoverIdleSaveHugeValue;
  readonly hovGainPerSecond: HoverIdleSaveHugeValue;
  readonly timGainPerSecond: HoverIdleSaveHugeValue;
  readonly hiddenGrowth: HoverIdleSaveHiddenGrowthState;
  readonly upgradeCost: HoverIdleSaveHugeValue;
  readonly timUpgradeCost: HoverIdleSaveHugeValue;
  readonly targetGrid: HoverIdleSaveTargetGridState;
  readonly minHovGainUnlockCostsByTargetIndex: readonly (HoverIdleSaveHugeValue | null)[];
  readonly minHovGainMilestoneTriggerCostsByMilestoneIndex: readonly (HoverIdleSaveHugeValue | null)[];
  readonly timTargetGrid: HoverIdleSaveTimTargetGridState;
  readonly mHov: HoverIdleSaveMHovState;
  readonly nHov?: HoverIdleSaveNHovState;
  readonly oHov: HoverIdleSaveOHovState;
  readonly lastAnyResetAtMs?: number;
  readonly isTimHovActivationEnabled: boolean;
  readonly mpm: HoverIdleSaveMpmState;
  readonly hovAmplifier?: HoverIdleSaveHovAmplifierState;
  readonly wall?: HoverIdleSaveWallState;
  readonly exploration?: ExplorationState;
}

export interface HoverIdleSaveWallState {
  readonly activeWallId: string | null;
  readonly mpWallAssignments?: MpWallAssignments | null;
  // Accumulated game-time elapsed for the active wall run, in milliseconds.
  // `null` when no wall is active. Persists across reloads so a paused run
  // resumes with the same elapsed and best-clear semantics.
  readonly wallRunElapsedMs: number | null;
  readonly stepWallBestClearMs: number | null;
  readonly priceWallBestClearMs?: number | null;
  readonly mpWallBestClearMs?: number | null;
  readonly freshnessWallBestClearMs?: number | null;
  readonly outerWallBestClearMs?: number | null;
  readonly splitWallBestClearMs?: number | null;
  readonly hgWallBestClearMs?: number | null;
  readonly stockWallBestClearMs?: number | null;
  readonly amplificationWallBestClearMs?: number | null;
  readonly stockWallNhovUsedCount?: number;
  // Game-time elapsed since the last Hov-run boundary inside an active
  // Freshness Wall run. Optional so old saves load without the field; the
  // normalizer treats it as `null` outside `freshness-wall`.
  readonly freshnessElapsedSinceHovRunMs?: number | null;
  readonly unseenClearedWallIds?: readonly string[];
}

export interface HoverIdleSaveCumulativeStatsState extends Omit<
  HoverIdleModelState["cumulativeStats"],
  | "totalHovEarned"
  | "totalTimEarned"
  | "totalTimSpent"
  | "lifetimeHovRefinedFromTim"
  | "maxHov"
  | "hovGainFamilyUpgradesPurchased"
  | "hasExecutedNewHovWithoutMergeHov"
  | "hasExecutedMergeHovWithOnlyOneHovTargetCell"
  | "hasExecutedNHovExceedingNotFullyFreeThreshold"
  | "hasExecutedMassiveMergeHov"
  | "hasExecutedOutsideMhov"
  | "hasExecutedOutsideNhov"
  | "redAmplifierFullChargeReached"
  | "totalHovCoreSpent"
  | "mhovExecutedByMpCount"
> {
  readonly totalHovEarned: HoverIdleSaveHugeValue;
  readonly totalTimEarned: HoverIdleSaveHugeValue;
  readonly totalTimSpent: HoverIdleSaveHugeValue;
  readonly lifetimeHovRefinedFromTim: HoverIdleSaveHugeValue;
  readonly maxHov: HoverIdleSaveHugeValue;
  // Additive record: legacy saves omit it, restore derives a safe fallback.
  readonly hovGainFamilyUpgradesPurchased?: number;
  // Additive record: legacy saves omit it, restore defaults it to false.
  readonly hasExecutedNewHovWithoutMergeHov?: boolean;
  // Additive record: legacy saves omit it, restore defaults it to false.
  readonly hasExecutedMergeHovWithOnlyOneHovTargetCell?: boolean;
  // Additive record: legacy saves omit it, restore defaults it to false.
  readonly hasExecutedNHovExceedingNotFullyFreeThreshold?: boolean;
  // Additive record: legacy saves omit it, restore defaults it to false.
  readonly hasExecutedMassiveMergeHov?: boolean;
  // Additive record: legacy saves omit it, restore defaults it to false.
  readonly hasExecutedOutsideMhov?: boolean;
  // Additive record: legacy saves omit it, restore defaults it to false.
  readonly hasExecutedOutsideNhov?: boolean;
  // Additive record: legacy saves omit it, restore defaults it to false.
  readonly redAmplifierFullChargeReached?: boolean;
  // Additive record: legacy saves omit it, restore defaults it to "0".
  readonly totalHovCoreSpent?: HoverIdleSaveHugeValue;
  // Additive record: legacy saves omit it, restore defaults it to 0.
  readonly mhovExecutedByMpCount?: number;
}

export interface HoverIdleSaveBundle {
  readonly state: HoverIdleModelState;
  readonly hovSkills: HovSkillState;
  readonly timSkills: TimSkillState;
  readonly achievements: AchievementsState;
  readonly records: RecordsState;
  readonly timestamps: HoverIdleSaveTimestamps;
  readonly offlineReward?: OfflineRewardSaveMetadata;
}

export const emptyAchievementsState: AchievementsState = {
  unlockedIds: [],
  seenIds: [],
};

export { emptyRecordsState };

export function createDefaultPaymentState(): HoverIdlePaymentState {
  return {
    token: 0,
    redeemedRewardCodes: [],
    purchasedPermanentItems: {
      addMousePointer: 0,
      hovGainPercent: 0,
      timGainPercent: 0,
    },
    entitlements: [],
    claimedEntitlements: [],
  };
}

export function createDefaultHovTargetGridState(): HoverIdleHovTargetGridState {
  return {
    gridTier: 1,
    themeHue: null,
    hovCells: createDefaultHovCellStates(HOV_CELL_BASE_COSTS),
  };
}

export function createDefaultTimTargetGridState(): HoverIdleTimTargetGridState {
  return {
    gridTier: 1,
    timCells: createDefaultTimCellStates(TIM_CELL_BASE_COSTS),
  };
}

function serializeNHovBranchExperienceByBranch(
  value: NHovState["branchExperienceByBranch"],
): HoverIdleSaveNHovBranchExperienceByBranch {
  const normalized = normalizeNHovBranchExperienceByBranch(value);
  return {
    hgAmplify: serializeHuge(normalized.hgAmplify),
    hgDiscount: serializeHuge(normalized.hgDiscount),
    hngScale: serializeHuge(normalized.hngScale),
    hgNHovEffectsToHng: serializeHuge(normalized.hgNHovEffectsToHng),
  };
}

function serializeNHovEffectBoundaryCaps(
  value: NHovState["effectBoundaryCaps"],
): NHovEffectBoundaryCaps | undefined {
  const normalized = normalizeNHovEffectBoundaryCaps(value);
  if (!normalized.hgDiscountCostDivisor) {
    return undefined;
  }
  return normalized;
}

function serializeNHovState(state: NHovState): HoverIdleSaveNHovState {
  const effectBoundaryCaps = serializeNHovEffectBoundaryCaps(
    state.effectBoundaryCaps,
  );
  const continuousEffectProgressByBranch =
    normalizeNHovContinuousEffectProgressByBranch(
      state.continuousEffectProgressByBranch,
    );
  return {
    count: state.count,
    branchExperienceByBranch: serializeNHovBranchExperienceByBranch(
      state.branchExperienceByBranch,
    ),
    ...(effectBoundaryCaps === undefined ? {} : { effectBoundaryCaps }),
    ...(Object.keys(continuousEffectProgressByBranch).length === 0
      ? {}
      : { continuousEffectProgressByBranch }),
  };
}

export function createHoverIdleSaveData(
  bundle: HoverIdleSaveBundle,
): HoverIdleSaveData {
  return {
    version: HOVER_IDLE_SAVE_DATA_VERSION,
    economy: {
      hov: serializeHuge(bundle.state.economy.hov),
      tim: serializeHuge(bundle.state.economy.tim),
      fai: bundle.state.economy.fai,
      hovGainPerSecond: serializeHuge(bundle.state.economy.hovGainPerSecond),
      timGainPerSecond: serializeHuge(bundle.state.economy.timGainPerSecond),
      hiddenGrowth: {
        hovGainBonus: serializeHuge(
          bundle.state.economy.hiddenGrowth.hovGainBonus,
        ),
        timGainBonus: serializeHuge(
          bundle.state.economy.hiddenGrowth.timGainBonus,
        ),
        hovTimProgress: bundle.state.economy.hiddenGrowth.hovTimProgress,
      },
      upgradeCost: serializeHuge(bundle.state.economy.upgradeCost),
      timUpgradeCost: serializeHuge(bundle.state.economy.timUpgradeCost),
      purchasedUpgrades: {
        hovGain: bundle.state.economy.purchasedUpgrades.hovGain,
        timGain: bundle.state.economy.purchasedUpgrades.timGain,
      },
      unlocks: {
        tabs: {
          hovTarget: bundle.state.economy.unlocks.tabs.hovTarget,
          hovTimBalance: bundle.state.economy.unlocks.tabs.hovTimBalance,
          hovTimGainBalance:
            bundle.state.economy.unlocks.tabs.hovTimGainBalance,
          hovSkill: bundle.state.economy.unlocks.tabs.hovSkill,
        },
        effects: {
          hov: {
            gridIncrease1:
              bundle.state.economy.unlocks.effects.hov.gridIncrease1,
            gridIncrease2:
              bundle.state.economy.unlocks.effects.hov.gridIncrease2,
          },
        },
      },
      prestige: {
        count: bundle.state.economy.prestige.count,
        points: bundle.state.economy.prestige.points,
      },
      targetGrid: cloneHovTargetGridSaveState(bundle.state.economy.targetGrid),
      minHovGainUnlockCostsByTargetIndex: serializeHovGainCostRecords(
        bundle.state.economy.minHovGainUnlockCostsByTargetIndex,
      ),
      minHovGainMilestoneTriggerCostsByMilestoneIndex:
        serializeHovGainCostRecords(
          bundle.state.economy.minHovGainMilestoneTriggerCostsByMilestoneIndex,
        ),
      timTargetGrid: cloneTimTargetGridSaveState(
        bundle.state.economy.timTargetGrid,
      ),
      hovTimBalance: { ...bundle.state.economy.hovTimBalance },
      mHov: {
        count: bundle.state.economy.mHov.count,
        hgMultiplier: serializeHuge(bundle.state.economy.mHov.hgMultiplier),
        currentLoopHovEarned: serializeHuge(
          bundle.state.economy.mHov.currentLoopHovEarned,
        ),
        maxHovSinceLastMHov: serializeHuge(
          bundle.state.economy.mHov.maxHovSinceLastMHov,
        ),
        lastRewardBaseHov: serializeHuge(
          bundle.state.economy.mHov.lastRewardBaseHov,
        ),
      },
      nHov: serializeNHovState(bundle.state.economy.nHov),
      oHov: {
        count: bundle.state.economy.oHov.count,
        isUnlocked: bundle.state.economy.oHov.isUnlocked,
        power: serializeHuge(bundle.state.economy.oHov.power),
        hovCore: serializeHuge(bundle.state.economy.oHov.hovCore),
        records: {
          currentRunStartedAtMs:
            bundle.state.economy.oHov.records.currentRunStartedAtMs,
          lastRunDurationMs:
            bundle.state.economy.oHov.records.lastRunDurationMs,
          fastestRunDurationMs:
            bundle.state.economy.oHov.records.fastestRunDurationMs,
          currentRunGameElapsedMs:
            bundle.state.economy.oHov.records.currentRunGameElapsedMs,
          fastestRunGameDurationMs:
            bundle.state.economy.oHov.records.fastestRunGameDurationMs,
        },
      },
      resetAutomation: {
        minimumGameplayIntervalCarryMs: {
          mHov:
            bundle.state.economy.resetAutomation?.minimumGameplayIntervalCarryMs
              .mHov ?? 0,
          nHov:
            bundle.state.economy.resetAutomation?.minimumGameplayIntervalCarryMs
              .nHov ?? 0,
          oHov:
            bundle.state.economy.resetAutomation?.minimumGameplayIntervalCarryMs
              .oHov ?? 0,
        },
      },
      lastAnyResetAtMs: bundle.state.economy.lastAnyResetAtMs,
      isTimHovActivationEnabled: bundle.state.economy.isTimHovActivationEnabled,
      mpm: serializeMpmStateForSave(
        bundle.state.economy.mpm ?? createDefaultMpmState(),
      ),
      hovAmplifier: serializeHovAmplifierStateForSave(
        bundle.state.economy.hovAmplifier ?? createInitialHovAmplifierState(),
      ),
      wall: bundle.state.economy.wall
        ? {
            activeWallId: bundle.state.economy.wall.activeWallId,
            mpWallAssignments:
              bundle.state.economy.wall.activeWallId === "mp-wall"
                ? normalizeMpWallAssignments(
                    bundle.state.economy.wall.mpWallAssignments,
                  )
                : null,
            wallRunElapsedMs: bundle.state.economy.wall.wallRunElapsedMs,
            stepWallBestClearMs: bundle.state.economy.wall.stepWallBestClearMs,
            priceWallBestClearMs:
              bundle.state.economy.wall.priceWallBestClearMs,
            mpWallBestClearMs: bundle.state.economy.wall.mpWallBestClearMs,
            freshnessWallBestClearMs:
              bundle.state.economy.wall.freshnessWallBestClearMs,
            outerWallBestClearMs:
              bundle.state.economy.wall.outerWallBestClearMs,
            splitWallBestClearMs:
              bundle.state.economy.wall.splitWallBestClearMs,
            hgWallBestClearMs: bundle.state.economy.wall.hgWallBestClearMs,
            stockWallBestClearMs:
              bundle.state.economy.wall.stockWallBestClearMs,
            amplificationWallBestClearMs:
              bundle.state.economy.wall.amplificationWallBestClearMs,
            stockWallNhovUsedCount:
              bundle.state.economy.wall.stockWallNhovUsedCount,
            freshnessElapsedSinceHovRunMs:
              bundle.state.economy.wall.freshnessElapsedSinceHovRunMs,
            unseenClearedWallIds: [
              ...bundle.state.economy.wall.unseenClearedWallIds,
            ],
          }
        : undefined,
      exploration: cloneExplorationState(
        bundle.state.economy.exploration ?? createDefaultExplorationState(),
      ),
    },
    settings: {
      reduceMotion: bundle.state.settings.reduceMotion,
      lowPowerMode: bundle.state.settings.lowPowerMode,
      language: bundle.state.settings.language,
      achievementNotification: bundle.state.settings.achievementNotification,
      wallNotificationMode: bundle.state.settings.wallNotificationMode,
      helpNotificationsEnabled: bundle.state.settings.helpNotificationsEnabled,
      readHelpTopicIds: [...bundle.state.settings.readHelpTopicIds],
      hovSkillHoldDurationMs: bundle.state.settings.hovSkillHoldDurationMs,
      wallStartChargeDurationMs:
        bundle.state.settings.wallStartChargeDurationMs,
      notation: bundle.state.settings.notation,
      firstAccessTutorialCompleted:
        bundle.state.settings.firstAccessTutorialCompleted,
      mHovAnimationSpeed: bundle.state.settings.mHovAnimationSpeed,
      mHovChargeDurationMs: bundle.state.settings.mHovChargeDurationMs,
      nHovAnimationSpeed: bundle.state.settings.nHovAnimationSpeed,
      nHovChargeDurationMs: bundle.state.settings.nHovChargeDurationMs,
      oHovChargeDurationMs: bundle.state.settings.oHovChargeDurationMs,
      outsideResetBurstAnimationEnabled:
        bundle.state.settings.outsideResetBurstAnimationEnabled,
      hoverToggleDuration: bundle.state.settings.hoverToggleDuration,
      hoverChargeDuration: bundle.state.settings.hoverChargeDuration,
      amplifierUpgradeGuardMs: bundle.state.settings.amplifierUpgradeGuardMs,
      hasSeenAvailableAmplifierUpgradeGuard:
        bundle.state.settings.hasSeenAvailableAmplifierUpgradeGuard,
      hasSeenMHovButton: bundle.state.settings.hasSeenMHovButton,
      hasSeenNHovButton: bundle.state.settings.hasSeenNHovButton,
    },
    cumulativeStats: {
      totalHovEarned: serializeHuge(
        bundle.state.cumulativeStats.totalHovEarned,
      ),
      totalTimEarned: serializeHuge(
        bundle.state.cumulativeStats.totalTimEarned,
      ),
      totalFaiEarned: bundle.state.cumulativeStats.totalFaiEarned,
      totalTimSpent: serializeHuge(bundle.state.cumulativeStats.totalTimSpent),
      lifetimeHovRefinedFromTim: serializeHuge(
        bundle.state.cumulativeStats.lifetimeHovRefinedFromTim,
      ),
      upgradesPurchased: bundle.state.cumulativeStats.upgradesPurchased,
      hovGainFamilyUpgradesPurchased:
        bundle.state.cumulativeStats.hovGainFamilyUpgradesPurchased,
      amplifierUpgradesPurchased:
        bundle.state.cumulativeStats.amplifierUpgradesPurchased,
      prestigeCount: bundle.state.cumulativeStats.prestigeCount,
      onlinePlaytimeSeconds: bundle.state.cumulativeStats.onlinePlaytimeSeconds,
      maxHov: serializeHuge(bundle.state.cumulativeStats.maxHov),
      hasExecutedNewHovWithoutMergeHov:
        bundle.state.cumulativeStats.hasExecutedNewHovWithoutMergeHov,
      hasExecutedMergeHovWithOnlyOneHovTargetCell:
        bundle.state.cumulativeStats
          .hasExecutedMergeHovWithOnlyOneHovTargetCell,
      hasExecutedNHovExceedingNotFullyFreeThreshold:
        bundle.state.cumulativeStats
          .hasExecutedNHovExceedingNotFullyFreeThreshold,
      hasExecutedMassiveMergeHov:
        bundle.state.cumulativeStats.hasExecutedMassiveMergeHov,
      hasExecutedOutsideMhov:
        bundle.state.cumulativeStats.hasExecutedOutsideMhov,
      hasExecutedOutsideNhov:
        bundle.state.cumulativeStats.hasExecutedOutsideNhov,
      redAmplifierFullChargeReached:
        bundle.state.cumulativeStats.redAmplifierFullChargeReached,
      totalHovCoreSpent: serializeHuge(
        bundle.state.cumulativeStats.totalHovCoreSpent,
      ),
      mhovExecutedByMpCount: bundle.state.cumulativeStats.mhovExecutedByMpCount,
    },
    payment: {
      token: bundle.state.payment.token,
      redeemedRewardCodes: [...bundle.state.payment.redeemedRewardCodes],
      purchasedPermanentItems: {
        addMousePointer:
          bundle.state.payment.purchasedPermanentItems.addMousePointer,
        hovGainPercent:
          bundle.state.payment.purchasedPermanentItems.hovGainPercent,
        timGainPercent:
          bundle.state.payment.purchasedPermanentItems.timGainPercent,
      },
      entitlements: [...bundle.state.payment.entitlements],
      claimedEntitlements: [...bundle.state.payment.claimedEntitlements],
    },
    skills: {
      hovAcquiredIds: [...bundle.hovSkills.acquiredIds],
      timAcquiredIds: [...bundle.timSkills.acquiredIds],
    },
    achievements: {
      unlockedIds: [...bundle.achievements.unlockedIds],
      seenIds: [...bundle.achievements.seenIds],
    },
    records: {
      purchaseHistory: bundle.records.purchaseHistory.map((entry) => ({
        ...entry,
      })),
      tokenTransactions: bundle.records.tokenTransactions.map((entry) => ({
        ...entry,
      })),
    },
    timestamps: {
      savedAt: bundle.timestamps.savedAt,
      firstPlayedAt: bundle.timestamps.firstPlayedAt,
    },
    offlineReward: cloneOfflineRewardSaveMetadata(
      bundle.offlineReward ??
        createDefaultOfflineRewardSaveMetadata(bundle.timestamps.savedAt),
    ),
  };
}

export function modelStateFromHoverIdleSaveData(
  saveData: HoverIdleSaveData,
): HoverIdleModelState {
  const hov = deserializeHuge(saveData.economy.hov);
  const hovSkillEffects = resolveHovSkillEffects({
    acquired: new Set(saveData.skills.hovAcquiredIds),
  });

  return {
    economy: {
      hov,
      tim: deserializeHuge(saveData.economy.tim),
      fai: saveData.economy.fai,
      hovGainPerSecond: deserializeHuge(saveData.economy.hovGainPerSecond),
      timGainPerSecond: deserializeHuge(saveData.economy.timGainPerSecond),
      hiddenGrowth: {
        hovGainBonus: deserializeHuge(
          saveData.economy.hiddenGrowth.hovGainBonus,
        ),
        timGainBonus: deserializeHuge(
          saveData.economy.hiddenGrowth.timGainBonus,
        ),
        hovTimProgress: saveData.economy.hiddenGrowth.hovTimProgress,
      },
      upgradeCost: deserializeHuge(saveData.economy.upgradeCost),
      timUpgradeCost: deserializeHuge(saveData.economy.timUpgradeCost),
      purchasedUpgrades: {
        hovGain: saveData.economy.purchasedUpgrades.hovGain,
        timGain: saveData.economy.purchasedUpgrades.timGain,
      },
      unlocks: {
        tabs: {
          hovTarget: saveData.economy.unlocks.tabs.hovTarget,
          hovTimBalance: saveData.economy.unlocks.tabs.hovTimBalance,
          hovTimGainBalance: saveData.economy.unlocks.tabs.hovTimGainBalance,
          hovSkill: saveData.economy.unlocks.tabs.hovSkill,
        },
        effects: {
          hov: {
            gridIncrease1: saveData.economy.unlocks.effects.hov.gridIncrease1,
            gridIncrease2: saveData.economy.unlocks.effects.hov.gridIncrease2,
          },
        },
      },
      prestige: {
        count: saveData.economy.prestige.count,
        points: saveData.economy.prestige.points,
      },
      targetGrid: cloneHovTargetGridState(saveData.economy.targetGrid),
      minHovGainUnlockCostsByTargetIndex: deserializeHovGainCostRecords(
        saveData.economy.minHovGainUnlockCostsByTargetIndex,
      ),
      minHovGainMilestoneTriggerCostsByMilestoneIndex:
        deserializeHovGainCostRecords(
          saveData.economy.minHovGainMilestoneTriggerCostsByMilestoneIndex,
        ),
      timTargetGrid: cloneTimTargetGridState(saveData.economy.timTargetGrid),
      hovTimBalance: { ...saveData.economy.hovTimBalance },
      mHov: {
        count: saveData.economy.mHov.count,
        hgMultiplier: deserializeHuge(saveData.economy.mHov.hgMultiplier),
        currentLoopHovEarned: deserializeHuge(
          saveData.economy.mHov.currentLoopHovEarned,
        ),
        maxHovSinceLastMHov: deserializeHuge(
          saveData.economy.mHov.maxHovSinceLastMHov,
        ),
        lastRewardBaseHov: deserializeHuge(
          saveData.economy.mHov.lastRewardBaseHov,
        ),
      },
      nHov: saveData.economy.nHov
        ? (() => {
            const branchEffectOverrides =
              normalizeLegacyNHovBranchEffectOverrides(
                saveData.economy.nHov.branchEffectOverrides,
              );
            const effectBoundaryCaps = normalizeNHovEffectBoundaryCaps(
              saveData.economy.nHov.effectBoundaryCaps,
            );
            const branchExperienceByBranch =
              normalizeNHovBranchExperienceByBranch(
                saveData.economy.nHov.branchExperienceByBranch,
              );
            const continuousEffectProgressByBranch = {
              ...normalizeNHovContinuousEffectProgressByBranch(
                saveData.economy.nHov.continuousEffectProgressByBranch,
              ),
            };
            for (const branchId of Object.keys(branchEffectOverrides)) {
              continuousEffectProgressByBranch[
                branchId as keyof typeof continuousEffectProgressByBranch
              ] = true;
            }
            return {
              count: saveData.economy.nHov.count,
              branchExperienceByBranch:
                Object.keys(branchEffectOverrides).length === 0
                  ? branchExperienceByBranch
                  : migrateLegacyNHovBranchEffectOverrides({
                      branchExperienceByBranch,
                      effectBoundaryCaps,
                      branchEffectOverrides,
                      hngRankScaleBaseBonus:
                        hovSkillEffects.hngRankScaleBaseBonus,
                      growthExponentBonus: {
                        hgAmplify:
                          hovSkillEffects.growthExponentBonus.nHovHgAmplify,
                        hgNHovEffectsToHng:
                          hovSkillEffects.growthExponentBonus
                            .nHovHgNHovEffectsToHng,
                        hngScale:
                          hovSkillEffects.growthExponentBonus.nHovHngScale,
                      },
                      uncapped:
                        saveData.cumulativeStats.hasExecutedOutsideNhov ===
                        true,
                    }),
              effectBoundaryCaps,
              ...(Object.keys(continuousEffectProgressByBranch).length === 0
                ? {}
                : { continuousEffectProgressByBranch }),
            };
          })()
        : createDefaultNHovState(),
      oHov: saveData.economy.oHov
        ? {
            count: saveData.economy.oHov.count,
            isUnlocked:
              saveData.economy.oHov.isUnlocked === true ||
              saveData.economy.oHov.count > 0 ||
              isOHovUnlocked(hov),
            power: deserializeHuge(saveData.economy.oHov.power),
            hovCore: deserializeHuge(saveData.economy.oHov.hovCore),
            records: normalizeOHovRunRecords(
              saveData.economy.oHov.records,
              saveData.timestamps.savedAt,
            ),
          }
        : createDefaultOHovState(saveData.timestamps.savedAt),
      resetAutomation: saveData.economy.resetAutomation
        ? {
            minimumGameplayIntervalCarryMs: {
              mHov: saveData.economy.resetAutomation
                .minimumGameplayIntervalCarryMs.mHov,
              nHov: saveData.economy.resetAutomation
                .minimumGameplayIntervalCarryMs.nHov,
              oHov: saveData.economy.resetAutomation
                .minimumGameplayIntervalCarryMs.oHov,
            },
          }
        : undefined,
      lastAnyResetAtMs: saveData.economy.lastAnyResetAtMs ?? 0,
      isTimHovActivationEnabled:
        saveData.economy.isTimHovActivationEnabled ?? true,
      mpm: normalizeMpmState(saveData.economy.mpm),
      hovAmplifier: normalizeHovAmplifierState(saveData.economy.hovAmplifier),
      wall: normalizeWallState(saveData.economy.wall),
      exploration: normalizeExplorationState(saveData.economy.exploration),
    },
    settings: {
      reduceMotion: saveData.settings.reduceMotion,
      lowPowerMode: saveData.settings.lowPowerMode ?? false,
      language: saveData.settings.language,
      achievementNotification: saveData.settings.achievementNotification,
      wallNotificationMode: saveData.settings.wallNotificationMode ?? "full",
      helpNotificationsEnabled:
        saveData.settings.helpNotificationsEnabled ?? true,
      readHelpTopicIds: normalizeHelpTopicIds(
        saveData.settings.readHelpTopicIds,
      ),
      hovSkillHoldDurationMs: saveData.settings.hovSkillHoldDurationMs,
      wallStartChargeDurationMs: isWallStartChargeMs(
        saveData.settings.wallStartChargeDurationMs,
      )
        ? saveData.settings.wallStartChargeDurationMs
        : DEFAULT_WALL_START_CHARGE_MS,
      notation: saveData.settings.notation,
      firstAccessTutorialCompleted:
        saveData.settings.firstAccessTutorialCompleted,
      mHovAnimationSpeed: saveData.settings.mHovAnimationSpeed,
      mHovChargeDurationMs: saveData.settings.mHovChargeDurationMs,
      nHovAnimationSpeed: saveData.settings.nHovAnimationSpeed,
      nHovChargeDurationMs: saveData.settings.nHovChargeDurationMs,
      oHovChargeDurationMs: saveData.settings.oHovChargeDurationMs,
      outsideResetBurstAnimationEnabled:
        saveData.settings.outsideResetBurstAnimationEnabled ?? true,
      hoverToggleDuration: saveData.settings.hoverToggleDuration ?? "normal",
      hoverChargeDuration: saveData.settings.hoverChargeDuration ?? "normal",
      amplifierUpgradeGuardMs: saveData.settings.amplifierUpgradeGuardMs ?? 0,
      hasSeenAvailableAmplifierUpgradeGuard:
        saveData.settings.hasSeenAvailableAmplifierUpgradeGuard ?? false,
      hasSeenMHovButton: saveData.settings.hasSeenMHovButton ?? false,
      hasSeenNHovButton: saveData.settings.hasSeenNHovButton ?? false,
    },
    cumulativeStats: {
      totalHovEarned: deserializeHuge(saveData.cumulativeStats.totalHovEarned),
      totalTimEarned: deserializeHuge(saveData.cumulativeStats.totalTimEarned),
      totalFaiEarned: saveData.cumulativeStats.totalFaiEarned,
      totalTimSpent: deserializeHuge(saveData.cumulativeStats.totalTimSpent),
      lifetimeHovRefinedFromTim: deserializeHuge(
        saveData.cumulativeStats.lifetimeHovRefinedFromTim,
      ),
      upgradesPurchased: saveData.cumulativeStats.upgradesPurchased,
      hovGainFamilyUpgradesPurchased:
        saveData.cumulativeStats.hovGainFamilyUpgradesPurchased ??
        deriveHovGainFamilyUpgradesPurchased(saveData.economy),
      amplifierUpgradesPurchased:
        saveData.cumulativeStats.amplifierUpgradesPurchased,
      prestigeCount: saveData.cumulativeStats.prestigeCount,
      onlinePlaytimeSeconds: saveData.cumulativeStats.onlinePlaytimeSeconds,
      maxHov: deserializeHuge(saveData.cumulativeStats.maxHov),
      hasExecutedNewHovWithoutMergeHov:
        saveData.cumulativeStats.hasExecutedNewHovWithoutMergeHov ?? false,
      hasExecutedMergeHovWithOnlyOneHovTargetCell:
        saveData.cumulativeStats.hasExecutedMergeHovWithOnlyOneHovTargetCell ??
        false,
      hasExecutedNHovExceedingNotFullyFreeThreshold:
        saveData.cumulativeStats
          .hasExecutedNHovExceedingNotFullyFreeThreshold ?? false,
      hasExecutedMassiveMergeHov:
        saveData.cumulativeStats.hasExecutedMassiveMergeHov ?? false,
      hasExecutedOutsideMhov:
        saveData.cumulativeStats.hasExecutedOutsideMhov ?? false,
      hasExecutedOutsideNhov:
        saveData.cumulativeStats.hasExecutedOutsideNhov ?? false,
      redAmplifierFullChargeReached:
        saveData.cumulativeStats.redAmplifierFullChargeReached ?? false,
      totalHovCoreSpent: deserializeHuge(
        saveData.cumulativeStats.totalHovCoreSpent,
      ),
      mhovExecutedByMpCount:
        saveData.cumulativeStats.mhovExecutedByMpCount ?? 0,
    },
    payment: {
      token: saveData.payment.token,
      redeemedRewardCodes: [...saveData.payment.redeemedRewardCodes],
      purchasedPermanentItems: {
        addMousePointer:
          saveData.payment.purchasedPermanentItems.addMousePointer,
        hovGainPercent: saveData.payment.purchasedPermanentItems.hovGainPercent,
        timGainPercent: saveData.payment.purchasedPermanentItems.timGainPercent,
      },
      entitlements: [...saveData.payment.entitlements],
      claimedEntitlements: [...saveData.payment.claimedEntitlements],
    },
  };
}

function serializeHovAmplifierStateForSave(
  state: HovAmplifierState,
): HoverIdleSaveHovAmplifierState {
  const cumulativeBoosts: Partial<
    Record<HovAmplifierEntryId, HoverIdleSaveHugeValue>
  > = {};
  for (const id of HOV_AMPLIFIER_ENTRY_IDS) {
    cumulativeBoosts[id] = serializeHuge(state.cumulativeBoosts[id]);
  }
  return {
    ap: serializeHuge(state.ap),
    apExponent: state.apExponent,
    entries: state.entries.map((entry) => ({
      id: entry.id,
      gaugeValue: entry.gaugeValue,
      enhancementLevel: entry.enhancementLevel,
    })),
    cumulativeBoosts,
  };
}

function deriveHovGainFamilyUpgradesPurchased(
  economy: HoverIdleSaveEconomyState,
): number {
  const globalPurchases = economy.purchasedUpgrades.hovGain;
  const cellPurchases = economy.targetGrid.hovCells.reduce(
    (total, cell) => total + cell.purchasedUpgrades,
    0,
  );
  return Math.max(0, globalPurchases + cellPurchases);
}

export function hovSkillStateFromHoverIdleSaveData(
  saveData: HoverIdleSaveData,
): HovSkillState {
  return {
    acquiredIds: [...saveData.skills.hovAcquiredIds],
  };
}

export function timSkillStateFromHoverIdleSaveData(
  saveData: HoverIdleSaveData,
): TimSkillState {
  return {
    acquiredIds: [...saveData.skills.timAcquiredIds],
  };
}

export function achievementsStateFromHoverIdleSaveData(
  saveData: HoverIdleSaveData,
): AchievementsState {
  return normalizeAchievementsState({
    unlockedIds: saveData.achievements.unlockedIds as readonly string[],
    seenIds: saveData.achievements.seenIds as readonly string[],
  });
}

export function recordsStateFromHoverIdleSaveData(
  saveData: HoverIdleSaveData,
): RecordsState {
  return {
    purchaseHistory: saveData.records.purchaseHistory.map((entry) => ({
      ...entry,
    })),
    tokenTransactions: saveData.records.tokenTransactions.map((entry) => ({
      ...entry,
    })),
  };
}

export function createEmptyHovSkillState(): HovSkillState {
  return {
    acquiredIds: [...emptyHovSkillState.acquiredIds],
  };
}

export function createEmptyTimSkillState(): TimSkillState {
  return {
    acquiredIds: [...emptyTimSkillState.acquiredIds],
  };
}

function createDefaultHovCellStates(
  baseCosts: readonly number[],
): readonly HoverIdleHovCellState[] {
  return Array.from({ length: 9 }, (_, index) => ({
    unlocked: index === 0,
    hovGainMultiplier: deserializeHuge(index === 0 ? 1 : 0),
    upgradeCost: resolveInitialHovCellUpgradeCost(
      index,
      baseCosts[index] ?? HOV_CELL_GAIN_UPGRADE_BASE_COST,
    ),
    purchasedUpgrades: 0,
  }));
}

function createDefaultTimCellStates(
  baseCosts: readonly number[],
): readonly HoverIdleTimCellState[] {
  return baseCosts.map((baseCost, index) => ({
    unlocked: index === 0,
    timGainMultiplier: deserializeHuge(index === 0 ? 1 : 0),
    upgradeCost: deserializeHuge(baseCost),
    purchasedUpgrades: 0,
  }));
}

function cloneHovTargetGridSaveState(
  state: HoverIdleHovTargetGridState,
): HoverIdleSaveTargetGridState {
  return {
    gridTier: state.gridTier,
    themeHue: normalizeNHovThemeHue(state.themeHue),
    hovCells: state.hovCells.map((cell) => ({
      unlocked: cell.unlocked,
      hovGainMultiplier: serializeHuge(cell.hovGainMultiplier),
      upgradeCost: serializeHuge(cell.upgradeCost),
      purchasedUpgrades: cell.purchasedUpgrades,
    })),
  };
}

function cloneHovTargetGridState(
  state: HoverIdleSaveTargetGridState,
): HoverIdleHovTargetGridState {
  return {
    gridTier: state.gridTier,
    themeHue: normalizeNHovThemeHue(state.themeHue),
    hovCells: state.hovCells.map((cell) => ({
      unlocked: cell.unlocked,
      hovGainMultiplier: deserializeHuge(cell.hovGainMultiplier),
      upgradeCost: deserializeHuge(cell.upgradeCost),
      purchasedUpgrades: cell.purchasedUpgrades,
    })),
  };
}

export function serializeMpmStateForSave(
  state: MpmState,
): HoverIdleSaveMpmState {
  const normalized = cloneMpmState(state);
  return {
    ...normalized,
    mhovExecution: {
      ...normalized.mhovExecution,
      multiplier: serializeHuge(normalized.mhovExecution.multiplier),
      runtime: { pending: false },
    },
    nhovExecution: {
      ...normalized.nhovExecution,
      conditions: serializeMpmNHovExecutionConditionsForSave(
        normalized.nhovExecution.conditions,
      ),
    },
    ohovExecution: {
      ...normalized.ohovExecution,
      minimumHovCore: serializeHuge(normalized.ohovExecution.minimumHovCore),
    },
    presets: normalized.presets.map((preset) => {
      if (preset === null) return null;
      return {
        ...preset,
        numericSettings:
          preset.numericSettings === null
            ? null
            : {
                mhovExecution: {
                  ...preset.numericSettings.mhovExecution,
                  multiplier: serializeHuge(
                    preset.numericSettings.mhovExecution.multiplier,
                  ),
                },
                nhovExecution: {
                  conditions: serializeMpmNHovExecutionConditionsForSave(
                    preset.numericSettings.nhovExecution.conditions,
                  ),
                },
                ohovExecution: {
                  ...preset.numericSettings.ohovExecution,
                  minimumHovCore: serializeHuge(
                    preset.numericSettings.ohovExecution.minimumHovCore,
                  ),
                },
              },
      };
    }),
  };
}

function serializeMpmNHovExecutionConditionsForSave(
  conditions: MpmNHovExecutionState["conditions"],
): Record<NHovBranchId, HoverIdleSaveMpmNHovExecutionConditionState> {
  return Object.fromEntries(
    Object.entries(conditions).map(([branchId, condition]) => [
      branchId,
      {
        ...condition,
        minimumEffect: serializeHuge(condition.minimumEffect),
      },
    ]),
  ) as Record<NHovBranchId, HoverIdleSaveMpmNHovExecutionConditionState>;
}

// Shared by the fixed per-cell unlock cost record and the variable-length HG
// milestone trigger cost record; both are nullable HugeNumber lists.
function serializeHovGainCostRecords(
  costs: readonly (HugeNumber | null)[],
): readonly (HoverIdleSaveHugeValue | null)[] {
  return costs.map((cost) => (cost === null ? null : serializeHuge(cost)));
}

function deserializeHovGainCostRecords(
  costs: readonly (HoverIdleSaveHugeValue | null)[],
): readonly (HugeNumber | null)[] {
  return costs.map((cost) => (cost === null ? null : deserializeHuge(cost)));
}

function cloneTimTargetGridSaveState(
  state: HoverIdleTimTargetGridState,
): HoverIdleSaveTimTargetGridState {
  return {
    gridTier: state.gridTier,
    timCells: state.timCells.map((cell) => ({
      unlocked: cell.unlocked,
      timGainMultiplier: serializeHuge(cell.timGainMultiplier),
      upgradeCost: serializeHuge(cell.upgradeCost),
      purchasedUpgrades: cell.purchasedUpgrades,
    })),
  };
}

function cloneTimTargetGridState(
  state: HoverIdleSaveTimTargetGridState,
): HoverIdleTimTargetGridState {
  return {
    gridTier: state.gridTier,
    timCells: state.timCells.map((cell) => ({
      unlocked: cell.unlocked,
      timGainMultiplier: deserializeHuge(cell.timGainMultiplier),
      upgradeCost: deserializeHuge(cell.upgradeCost),
      purchasedUpgrades: cell.purchasedUpgrades,
    })),
  };
}
